import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CourierAssignmentFilters,
  CourierAssignmentFormData,
  CourierFormData,
  CourierSettlementPayload,
} from "../Types";
import {
  assignCourierToOrder,
  deleteCourier,
  getCourierAssignments,
  getCouriers,
  saveCourier,
  unassignCourier,
  updateCourierAssignmentFeePayer,
  updateCourierAssignmentStatus,
  getCourierLedger,
  settleCourierPayments,
  getCourierPayments,
} from "../Api";

export const courierQueryKeys = {
  all: ["couriers"] as const,
  assignments: ["courier-assignments"] as const,
};

export const useCourierQuery = () => {
  return useQuery({
    queryKey: courierQueryKeys.all,
    queryFn: () => getCouriers(),
  });
};

export const useCourierSaveMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CourierFormData) => saveCourier(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courierQueryKeys.all });
    },
  });
};

export const useCourierDeleteMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCourier(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courierQueryKeys.all });
    },
  });
};

export const useCourierAssignMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CourierAssignmentFormData) => assignCourierToOrder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courierQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: courierQueryKeys.assignments });
    },
  });
};

export const useCourierAssignmentStatusUpdateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      assignment_id: string;
      status: string;
      notes?: string;
    }) => updateCourierAssignmentStatus(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courierQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: courierQueryKeys.assignments });
      queryClient.invalidateQueries({ queryKey: courierLedgerQueryKeys.all });
    },
  });
};

export const useCourierAssignmentFeePayerMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { assignment_id: string; fee_payer: string }) =>
      updateCourierAssignmentFeePayer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courierQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: courierQueryKeys.assignments });
    },
  });
};

export const useCourierUnassignMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (assignment_id: string) => unassignCourier(assignment_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courierQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: courierQueryKeys.assignments });
    },
  });
};

export const useCourierAssignmentsQuery = (
  filters: CourierAssignmentFilters = {},
) => {
  return useQuery({
    queryKey: [...courierQueryKeys.assignments, filters],
    queryFn: () => getCourierAssignments(filters),
  });
};

export const courierLedgerQueryKeys = {
  all: ["courier-ledger"] as const,
};

export const courierPaymentsQueryKeys = {
  all: ["courier-payments"] as const,
};

export const useCourierLedgerQuery = (filters: {
  courier_id?: string;
  start_date?: string;
  end_date?: string;
  settled_status?: string;
  status?: string;
}) => {
  return useQuery({
    queryKey: [...courierLedgerQueryKeys.all, filters],
    queryFn: () => getCourierLedger(filters),
    enabled: !!filters.courier_id,
  });
};

export const useCourierSettleMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CourierSettlementPayload) => settleCourierPayments(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: courierQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: courierQueryKeys.assignments });
      queryClient.invalidateQueries({ queryKey: courierLedgerQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: courierPaymentsQueryKeys.all });
    },
  });
};

export const useCourierPaymentsQuery = (filters: {
  courier_id?: string;
  page?: number;
  limit?: number;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: [...courierPaymentsQueryKeys.all, filters],
    queryFn: () => getCourierPayments(filters),
    enabled: filters.enabled,
  });
};
